import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import Dispatch, { RANGES } from './Dispatch';
import { dispatchCopy } from './dispatchCopy';

/*
 * Pengambil data untuk layar Dispatch.
 *
 * Fetch biasa ke /graphql dari peramban, bukan Apollo — sama seperti halaman
 * Website Content. Layar ini butuh dua query dan tidak butuh cache, normalisasi,
 * maupun langganan; menyeret Apollo ke dalamnya cuma menambah lapisan tanpa
 * menjawab apa pun.
 *
 * Dijalankan setelah mount, bukan saat SSR: kerangka dan navigasinya tetap
 * tampil seketika, dan angkanya menyusul. Dengan data prod sekarang angka itu
 * hampir semuanya nol, jadi tidak ada gunanya menahan render halaman demi
 * menunggunya.
 */
const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q_OVERVIEW = `query ($range: String) {
  getDispatchOverview(range: $range) {
    status errorMessage range currency
    gross { value prev } commission { value prev } bookings { value prev }
    completed { value prev } newCustomers { value prev } newProviders { value prev }
    avgJobValue { value prev }
    series { label gross bookings completed customers providers }
    pipeline { key value } outcomes { key value }
    unassignedDirect activeJobs failedPayouts oldestUnassignedMins
    allTimeGross medianAcceptSecs medianArrivalSecs cancelRate
    lastBookingDaysAgo lastCompletedDaysAgo
  }
}`;

const Q_PROVIDERS = `{
  getOnlineProviders { summary { online offline } result { id name location isActive activeStatus activeService } }
}`;

class DispatchContainer extends React.Component {
  // 30 hari, bukan hari ini: tidak ada hari dengan transaksi di produksi.
  state = { range: '30d', data: null, providers: null, loading: true, error: null };

  componentDidMount() {
    this.load(this.state.range);
    this.loadProviders();
    /*
     * Kartu armada bertuliskan "Live", jadi harus benar-benar hidup. 30 detik
     * cukup untuk konsol operasi dan tetap sepele bagi server — layar ini satu
     * query, dan yang membukanya paling banyak beberapa orang.
     *
     * Menyegarkan TANPA menyalakan status loading: kalau tidak, seluruh layar
     * berkedip jadi "—" tiap setengah menit.
     */
    this.timer = setInterval(() => {
      this.load(this.state.range, true);
      this.loadProviders();
    }, 30000);
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  load = async (range, quiet) => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q_OVERVIEW, { range }).catch(() => null);
    const res = json && json.data && json.data.getDispatchOverview;
    if (!res || res.status !== 200) {
      this.setState({ loading: false, error: (res && res.errorMessage) || true });
      return;
    }
    this.setState({ data: res, loading: false });
  };

  loadProviders = async () => {
    const json = await gql(Q_PROVIDERS).catch(() => null);
    const res = json && json.data && json.data.getOnlineProviders;
    if (res) this.setState({ providers: res });
  };

  setRange = range => {
    this.setState({ range });
    this.load(range);
  };

  /*
   * Export CSV dibuat di peramban dari data yang SUDAH tampil, bukan lewat
   * endpoint /export-admin-data yang sudah ada — endpoint itu mengekspor daftar
   * baris (booking, user), sedangkan yang diminta di sini ringkasan yang sedang
   * dilihat. Tidak ada gunanya pergi ke server untuk angka yang sudah ada di
   * memori.
   *
   * Tiap sel dibungkus tanda kutip dan kutip di dalamnya digandakan. Nama
   * provider di data ini memuat koma dan huruf Arab; tanpa itu satu nama bisa
   * memecah barisnya sendiri.
   */
  exportCsv = () => {
    const { data, range } = this.state;
    if (!data) return;
    const locale = this.props.intl.locale;
    const t = dispatchCopy(locale);
    const cell = v => '"' + String(v === null || v === undefined ? '' : v).replace(/"/g, '""') + '"';
    const m = k => (data[k] && data[k].value) || 0;
    const rows = [
      [t.csv.metric, t.csv.value, t.csv.previous],
      [t.csv.range, t.ranges[range], ''],
      [`${t.grossBookings} (${data.currency})`, m('gross'), (data.gross || {}).prev],
      [`${t.csv.commission} (${data.currency})`, m('commission'), (data.commission || {}).prev],
      [t.bookings, m('bookings'), (data.bookings || {}).prev],
      [t.completed, m('completed'), (data.completed || {}).prev],
      [t.newCustomers, m('newCustomers'), (data.newCustomers || {}).prev],
      [t.newProviders, m('newProviders'), (data.newProviders || {}).prev],
      [`${t.avgJobValue} (${data.currency})`, m('avgJobValue'), (data.avgJobValue || {}).prev],
      [t.csv.directUnassigned, data.unassignedDirect, ''],
      [],
      [t.csv.date, t.csv.gross, t.bookings, t.completed, t.csv.customers, t.csv.providers],
      ...(data.series || []).map(s => [s.label, s.gross, s.bookings, s.completed, s.customers, s.providers])
    ];
    const csv = rows.map(r => r.map(cell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `bas-dispatch-${range}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  render() {
    const { range, data, providers, loading, error } = this.state;
    const locale = this.props.intl.locale;
    const t = dispatchCopy(locale);
    return (
      <AdminShell
        current="dispatch"
        badges={data ? {
          activeJobs: data.activeJobs,
          unassignedDirect: data.unassignedDirect,
          failedPayouts: data.failedPayouts
        } : null}
        breadcrumb={[t.overview, t.dispatch]}
        title={t.dispatch}
        lead={t.lead}
        actions={[
          <div className="seg" role="group" aria-label={t.dateRange} key="seg">
            {RANGES.map(([k]) => (
              <button key={k} type="button" aria-pressed={String(range === k)}
                onClick={() => this.setRange(k)}>{t.ranges[k]}</button>
            ))}
          </div>,
          <button className="btn ghost" type="button" key="x" onClick={this.exportCsv}
            disabled={!data}>
            <Icon name="dl" />{t.export}
          </button>
        ]}
      >
        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">
            {error === true ? t.loadError : error}
          </span></div>
          : <Dispatch data={data} providers={providers} range={range}
            onRange={this.setRange} loading={loading} locale={locale} />}
      </AdminShell>
    );
  }
}

export default injectIntl(DispatchContainer);
